import { useEffect, useMemo, useState } from "react";
import { RefreshCwIcon, Trash2Icon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { useIsAdmin } from "@/hooks/use-is-admin";
import { SKILL_BODY_MAX_LENGTH, SKILL_DESCRIPTION_MAX_LENGTH, useSkills } from "@/hooks/use-skills";
import { cn } from "@/lib/utils";
import { SaveFab } from "./save-fab";
import { ScopeBadge } from "./scope-badge";
import { SettingsPageHeader, type BreadcrumbSegment } from "./settings-page-header";

type SkillDetailMode = "personal" | "admin";

type SkillFormState = {
  name: string;
  description: string;
  body: string;
};

const INPUT_CLASS =
  "mt-2 w-full rounded-[10px] border border-border bg-background px-3 py-2.5 text-[14px] text-foreground outline-none transition-shadow placeholder:text-muted-foreground focus:border-ring focus:shadow-[0_0_0_3px_oklch(0.55_0.03_264/0.10)]";
const READONLY_CLASS =
  "cursor-default bg-muted/30 text-muted-foreground focus:border-border focus:shadow-none";

export function SkillDetail({ id, mode = "personal" }: { id?: string; mode?: SkillDetailMode }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const basePath = mode === "admin" ? "/settings/admin/skills" : "/settings/skills";
  const { skills, loading, error, refresh, updateSkill, deleteSkill } =
    useSkills();
  const [form, setForm] = useState<SkillFormState>({
    name: "",
    description: "",
    body: "",
  });
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const skill = useMemo(
    () => skills.find((entry) => entry.id === id),
    [skills, id],
  );

  // Can edit: user skills → always (they belong to you), tenant skills → admin only
  const canEdit = skill
    ? skill.scope === "user" || (skill.scope === "tenant" && isAdmin)
    : false;

  useEffect(() => {
    if (!skill) return;
    setForm({ name: skill.name, description: skill.description, body: skill.body });
    setSubmitError(null);
  }, [skill]);

  const isDirty =
    canEdit &&
    skill != null &&
    (form.name !== skill.name ||
      form.description !== skill.description ||
      form.body !== skill.body);

  const resolvedError =
    error ?? (!loading && (!id || !skill) ? t("error.code.not_found") : null);

  const handleFieldChange = (field: keyof SkillFormState, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSave = async () => {
    if (!skill || !isDirty || saving) return;

    setSaving(true);
    setSubmitError(null);
    try {
      const updated = await updateSkill(skill.id, form);
      setForm({
        name: updated.name,
        description: updated.description,
        body: updated.body,
      });
      toast.success(t("settings.savedToast"));
    } catch (err) {
      setSubmitError(
        err instanceof Error
          ? err.message
          : t("error.code.service_unavailable"),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!skill || deleting) return;

    setDeleting(true);
    setSubmitError(null);
    try {
      await deleteSkill(skill.id);
      navigate(basePath, { replace: true });
    } catch (err) {
      setSubmitError(
        err instanceof Error
          ? err.message
          : t("error.code.service_unavailable"),
      );
      setDeleting(false);
      setDeleteOpen(false);
      return;
    }
  };

  const breadcrumbs: BreadcrumbSegment[] =
    mode === "admin"
      ? [
          { labelKey: "settings.breadcrumb.root", href: "/settings" },
          { labelKey: "settings.nav.administration", href: "/settings/admin" },
          { labelKey: "settings.nav.adminSkills", href: "/settings/admin/skills" },
          { labelKey: "settings.skills.detail.title" },
        ]
      : [
          { labelKey: "settings.breadcrumb.root", href: "/settings" },
          { labelKey: "settings.nav.skills", href: "/settings/skills" },
          { labelKey: "settings.skills.detail.title" },
        ];

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-y-auto px-8 pt-8 pb-10">
        <div className="mx-auto max-w-[880px] space-y-6">
          <SettingsPageHeader
            breadcrumbs={breadcrumbs}
            titleKey="settings.skills.detail.title"
            backHref={basePath}
            titleTrailing={skill && <ScopeBadge scope={skill.scope} />}
            trailing={
              canEdit && skill ? (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setDeleteOpen(true)}
                >
                  <Trash2Icon className="size-4" />
                  {t("settings.skills.detail.deleteButton")}
                </Button>
              ) : undefined
            }
          />

          {resolvedError && !skill && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-destructive/25 bg-destructive/5 px-4 py-3 text-[13px] text-destructive">
              <span>{resolvedError}</span>
              <Button variant="outline" size="sm" onClick={refresh}>
                <RefreshCwIcon className="size-4" />
                {t("common.retry")}
              </Button>
            </div>
          )}

          {loading && !skill ? (
            <div className="flex items-center gap-2 rounded-lg border border-border/60 bg-muted/20 px-4 py-3 text-[13px] text-muted-foreground">
              <Spinner size="sm" />
              <span>{t("common.loading")}</span>
            </div>
          ) : skill ? (
            <>
              {submitError && (
                <div className="rounded-lg border border-destructive/25 bg-destructive/5 px-4 py-3 text-[13px] text-destructive">
                  {submitError}
                </div>
              )}

              <div className="grid gap-6">
                <FieldBlock label={t("settings.skills.detail.nameLabel")}>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(event) =>
                      handleFieldChange("name", event.target.value)
                    }
                    readOnly={!canEdit}
                    placeholder={t("settings.skills.placeholder.name")}
                    className={cn(INPUT_CLASS, !canEdit && READONLY_CLASS)}
                  />
                </FieldBlock>

                <FieldBlock
                  label={t("settings.skills.detail.descriptionLabel")}
                  trailing={
                    <span
                      className={cn(
                        "text-[12px] text-muted-foreground tabular-nums",
                        form.description.length >= SKILL_DESCRIPTION_MAX_LENGTH &&
                          "text-destructive",
                      )}
                    >
                      {form.description.length}/{SKILL_DESCRIPTION_MAX_LENGTH}
                    </span>
                  }
                >
                  <textarea
                    value={form.description}
                    onChange={(event) =>
                      handleFieldChange("description", event.target.value)
                    }
                    readOnly={!canEdit}
                    spellCheck={false}
                    rows={3}
                    maxLength={SKILL_DESCRIPTION_MAX_LENGTH}
                    placeholder={t("settings.skills.placeholder.description")}
                    className={cn(
                      INPUT_CLASS,
                      "min-h-[80px] resize-y font-mono text-[13px]",
                      !canEdit && READONLY_CLASS,
                    )}
                  />
                </FieldBlock>

                <FieldBlock
                  label={t("settings.skills.detail.bodyLabel")}
                  trailing={
                    <span
                      className={cn(
                        "text-[12px] text-muted-foreground tabular-nums",
                        form.body.length >= SKILL_BODY_MAX_LENGTH &&
                          "text-destructive",
                      )}
                    >
                      {form.body.length}/{SKILL_BODY_MAX_LENGTH}
                    </span>
                  }
                >
                  <textarea
                    value={form.body}
                    onChange={(event) =>
                      handleFieldChange("body", event.target.value)
                    }
                    readOnly={!canEdit}
                    spellCheck={false}
                    rows={18}
                    maxLength={SKILL_BODY_MAX_LENGTH}
                    placeholder={t("settings.skills.placeholder.body")}
                    className={cn(
                      INPUT_CLASS,
                      "min-h-[360px] resize-y font-mono text-[13px] leading-relaxed",
                      !canEdit && READONLY_CLASS,
                    )}
                  />
                </FieldBlock>
              </div>
            </>
          ) : null}
        </div>
      </div>

      <SaveFab visible={isDirty} loading={saving} onClick={handleSave} />

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("settings.skills.detail.deleteButton")}
            </DialogTitle>
            <DialogDescription>
              {t("settings.skills.detail.deleteConfirm")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteOpen(false)}
              disabled={deleting}
            >
              {t("saveFresh.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? (
                <Spinner size="sm" />
              ) : (
                <Trash2Icon className="size-4" />
              )}
              {t("settings.skills.detail.deleteButton")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FieldBlock({
  label,
  trailing,
  children,
}: {
  label: string;
  trailing?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="grid gap-2">
      <div className="flex items-center justify-between gap-3">
        <div className="font-medium text-[13.5px] text-foreground">{label}</div>
        {trailing}
      </div>
      {children}
    </section>
  );
}
